import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import { useGeminiLanguage } from '../hooks/useGeminiLanguage';
import { getCanvasMessages } from '../utils/canvasI18n';
import HowToPinModal from './HowToPinModal';

/** Survives board remounts in this tab; cleared on reload. */
let sessionDismissed = false;

const IGNORED_SHAPE_TYPES = new Set(['suggest-pin']);

function pageHasUserShapes(editor) {
  if (!editor) return false;
  try {
    return editor.getCurrentPageShapes().some(shape => (
      shape?.type && !IGNORED_SHAPE_TYPES.has(shape.type)
    ));
  } catch {
    return false;
  }
}

/**
 * Non-blocking empty-canvas card. Teaches web pin, then anything else, and
 * opens HowToPinModal. Hidden after the first pin/paste or a session dismiss.
 */
const BoardEmptyOnboarding = ({
  editorRef,
  canEdit = false,
  pinCount = 0,
  canvasReady = false,
}) => {
  const { geminiLanguage } = useGeminiLanguage();
  const messages = getCanvasMessages(geminiLanguage);
  const [dismissed, setDismissed] = useState(sessionDismissed);
  const [guideOpen, setGuideOpen] = useState(false);
  const [hasCanvasShapes, setHasCanvasShapes] = useState(false);

  useEffect(() => {
    if (!canvasReady || !canEdit) return undefined;
    const editor = editorRef?.current;
    if (!editor) return undefined;

    const sync = () => setHasCanvasShapes(pageHasUserShapes(editor));
    sync();
    return editor.store.listen(sync, { scope: 'document' });
  }, [canvasReady, canEdit, editorRef, pinCount]);

  const showCard = Boolean(
    canEdit
    && canvasReady
    && !dismissed
    && Number(pinCount || 0) === 0
    && !hasCanvasShapes
  );

  const dismiss = () => {
    sessionDismissed = true;
    setDismissed(true);
    setGuideOpen(false);
  };

  if (!showCard && !guideOpen) return null;

  const steps = [
    { Icon: ExtensionOutlinedIcon, label: messages.howToPinStep1Title },
    { Icon: ContentPasteOutlinedIcon, label: messages.howToPinStep2Title },
  ];

  return (
    <>
      {showCard ? (
        <Box className="board-empty-onboarding" role="region" aria-label={messages.emptyOnboardingAria}>
          <Box className="board-empty-onboarding-card">
            <Box className="board-empty-onboarding-header">
              <Typography className="board-empty-onboarding-title">
                {messages.emptyOnboardingTitle}
              </Typography>
              <IconButton
                size="small"
                aria-label={messages.emptyOnboardingDismiss}
                onClick={dismiss}
                className="board-empty-onboarding-close"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography className="board-empty-onboarding-body">
              {messages.emptyOnboardingBody}
            </Typography>
            <ul className="board-empty-onboarding-steps">
              {steps.map(({ Icon, label }) => (
                <li key={label} className="board-empty-onboarding-step">
                  <Icon fontSize="small" className="board-empty-onboarding-step-icon" />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
            <Box className="board-empty-onboarding-actions">
              <Button
                size="small"
                variant="contained"
                disableElevation
                onClick={() => setGuideOpen(true)}
              >
                {messages.emptyOnboardingSeeHow}
              </Button>
            </Box>
          </Box>
        </Box>
      ) : null}
      <HowToPinModal
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        messages={messages}
      />
    </>
  );
};

export default BoardEmptyOnboarding;
